import { createFileRoute } from '@tanstack/react-router';
import {
  Alert,
  Box,
  Checkbox,
  Divider,
  Grid,
  Group,
  NumberInput,
  Select,
  Stack,
  Text,
  TextInput,
} from '@mantine/core';
import {
  AlertTriangle,
  Blocks,
  Download,
  Network,
  Plus,
  Server,
  ShieldAlert,
  Skull,
  Terminal,
  Trash2,
  Workflow,
} from 'lucide-react';
import { useCallback, useMemo, useState } from 'react';
// js-yaml ships no bundled types and @types/js-yaml is not a dependency, so
// under noImplicitAny tsc cannot resolve a declaration. We only use the parse
// (`load`) function; suppress the missing-types error at the import site.
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-expect-error -- untyped module, see note above
import { load as parseYamlRaw } from 'js-yaml';

const parseYaml = parseYamlRaw as (input: string) => unknown;
import ReactFlow, {
  Background,
  BackgroundVariant,
  Controls,
  type Edge,
  Handle,
  type Node,
  type NodeProps,
  Position,
} from 'reactflow';
import 'reactflow/dist/style.css';
import { motion } from 'framer-motion';
import { Badge, Button, Card, PageContainer, PageHeader, Pill, Section } from '../components/ui';
import { cardItem, staggerContainer } from '../lib/motion';
import { useBuilderValidate, useBuilderCostPreview } from '../hooks/useBuilder';
import { ValidatePane } from '../components/builder/ValidatePane';
import { CostPane } from '../components/builder/CostPane';
import { alpha, colors } from '../theme/tokens';

/**
 * /builder - drag-drop scenario authoring.
 *
 * MVP scope (operator decision 2026-05-25):
 *   * VM palette on the left, click-to-add (drag-drop is the same UX given
 *     ReactFlow needs the segment context to place the node).
 *   * ReactFlow canvas with one column per network segment, color-coded
 *     by kind (mirrors BlueprintCanvas viewer convention).
 *   * Right inspector: edit selected VM's hostname, role, instance_type,
 *     network_segment, depends_on.
 *   * Inter-segment rules editor under the canvas.
 *   * Live YAML preview matching services/shared/schemas/scenario.schema.json.
 *   * Download button writes scenario.yaml to disk.
 *   * No backend POST yet - operator drops the YAML into scenarios/<slug>/
 *     manually. (Catalog mutation endpoint is a later sprint.)
 */

export const Route = createFileRoute('/builder')({
  component: ScenarioBuilder,
});

// ---------- palette ------------------------------------------------------

interface PaletteEntry {
  role: string;
  label: string;
  defaultInstance: string;
  defaultSegment: string;
  icon: typeof Server;
}

const PALETTE: ReadonlyArray<PaletteEntry> = [
  { role: 'caldera-attacker', label: 'Caldera attacker', defaultInstance: 't3.medium', defaultSegment: 'attacker-net', icon: Skull },
  { role: 'linux-router', label: 'Linux router (FW)', defaultInstance: 't3.small', defaultSegment: 'external', icon: Network },
  { role: 'opnsense-fw', label: 'OPNsense firewall', defaultInstance: 't3.small', defaultSegment: 'external', icon: Network },
  { role: 'wazuh-manager', label: 'Wazuh manager (EDR)', defaultInstance: 't3.medium', defaultSegment: 'internal', icon: ShieldAlert },
  { role: 'elastic-stack', label: 'Elastic stack (SIEM)', defaultInstance: 'm5.large', defaultSegment: 'internal', icon: ShieldAlert },
  { role: 'base-ubuntu-22', label: 'Ubuntu 22.04', defaultInstance: 't3.medium', defaultSegment: 'dmz', icon: Server },
  { role: 'windows-workstation', label: 'Windows trainee', defaultInstance: 't3.large', defaultSegment: 'trainee', icon: Terminal },
  { role: 'windows-dc', label: 'Windows DC', defaultInstance: 't3.medium', defaultSegment: 'internal', icon: Server },
];

const INSTANCE_TYPES = ['t3.small', 't3.medium', 't3.large', 't3.xlarge', 'm5.large', 'm5.xlarge'];

const INSTANCE_COST_HR: Record<string, number> = {
  't3.small': 0.021,
  't3.medium': 0.042,
  't3.large': 0.083,
  't3.xlarge': 0.166,
  'm5.large': 0.096,
  'm5.xlarge': 0.192,
};

// ---------- segments ----------------------------------------------------

const DEFAULT_SEGMENTS: ReadonlyArray<SegmentDraft> = [
  { name: 'attacker-net', kind: 'attacker', cidr_override: '185.220.100.0/24', vlan_id: 100 },
  { name: 'external', kind: 'external', cidr_override: '45.142.0.0/24', vlan_id: 10 },
  { name: 'dmz', kind: 'dmz', cidr_override: '10.0.0.0/27', vlan_id: 20, public_facing: true },
  { name: 'internal', kind: 'internal', cidr_override: '10.0.0.32/27', vlan_id: 30 },
  { name: 'trainee', kind: 'user', cidr_override: '10.0.0.64/27', vlan_id: 40 },
];

const SEGMENT_KINDS = ['external', 'dmz', 'user', 'server', 'restricted', 'internal', 'defender', 'attacker', 'mgmt'] as const;

const SEGMENT_COLOUR: Record<string, string> = {
  attacker: '#3a1a1a',
  external: '#2a1f3a',
  mgmt: '#1f1f2b',
  dmz: '#1f2b2b',
  internal: '#13202b',
  user: '#1c1f2b',
  server: '#13202b',
  restricted: '#13202b',
  defender: '#13202b',
};

// ---------- drafts ------------------------------------------------------

interface SegmentDraft {
  name: string;
  kind: typeof SEGMENT_KINDS[number];
  cidr_override?: string;
  vlan_id?: number;
  public_facing?: boolean;
}

interface PerVmFirewallRule {
  direction: 'inbound' | 'outbound';
  action: 'allow' | 'deny';
  protocol: 'tcp' | 'udp' | 'icmp' | 'any';
  ports?: ReadonlyArray<number>;
  /** Free-form target: CIDR, hostname of another VM in the scenario, or "any". */
  target: string;
}

interface FirewallInterfaceBinding {
  /** Segment name this interface attaches to. */
  segment: string;
  /** Role of the interface (wan/lan/dmz/mgmt). */
  role: 'wan' | 'lan' | 'dmz' | 'mgmt' | 'trunk';
}

interface VmDraft {
  id: string;
  hostname: string;
  role: string;
  ami_ref: string;
  instance_type: string;
  network_segment: string;
  depends_on: ReadonlyArray<string>;
  count?: number;
  /** Free positioning on the canvas (operator can drag anywhere). */
  position?: { x: number; y: number };
  /** windows-dc only - domain config baked in at deploy. */
  dc_config?: {
    domain_fqdn: string;
    netbios?: string;
    ou_tree?: string;
  };
  /** windows-workstation / base-windows-10 only - hostname of DC to join. */
  domain_join?: {
    dc_vm_hostname: string;
    ou?: string;
  };
  /** Firewall VMs - which segments each interface attaches to. */
  firewall_interfaces?: ReadonlyArray<FirewallInterfaceBinding>;
  /** Per-VM firewall rules (in addition to scenario inter_segment_rules). */
  firewall_rules?: ReadonlyArray<PerVmFirewallRule>;
  /** Agents to install at deploy. Each agent points at a server VM in the
   *  scenario - deploy-time SSM resolves to that VM's private IP. */
  agents?: {
    wazuh?: { manager_vm: string };
    elastic?: { manager_vm: string; policy_id?: string };
    caldera?: { server_vm: string };
    sysmon?: boolean;
  };
}

interface InterSegRule {
  from: string;
  to: string;
  action: 'allow' | 'deny';
  protocol: 'tcp' | 'udp' | 'icmp' | 'any';
  ports?: ReadonlyArray<number>;
}

// ---------- ReactFlow node ----------------------------------------------

const NODE_WIDTH = 200;
const NODE_HEIGHT = 64;
const SEGMENT_HEADER = 48;

function VmNodeView({ data, selected }: NodeProps<{ vm: VmDraft; Icon: typeof Server }>) {
  const { vm, Icon } = data;
  return (
    <div
      style={{
        background: colors.card,
        border: `1px solid ${selected ? colors.hudCyan : '#2a2a2d'}`,
        borderRadius: 8,
        padding: '8px 10px',
        width: NODE_WIDTH,
        cursor: 'pointer',
      }}
    >
      <Handle type="target" position={Position.Left} style={{ background: colors.hudCyan }} />
      <Stack gap={2}>
        <Group gap={6} wrap="nowrap">
          <Icon size={13} color={colors.hudCyan} />
          <Text size="xs" c="rgb(var(--c-txt-bright))" fw={700} truncate>
            {vm.hostname}
          </Text>
        </Group>
        <Group gap={4} wrap="nowrap">
          <Pill variant="neutral" size="xs">
            {vm.role}
          </Pill>
          <Pill variant="warn" size="xs">
            {vm.instance_type}
          </Pill>
        </Group>
      </Stack>
      <Handle type="source" position={Position.Right} style={{ background: colors.hudCyan }} />
    </div>
  );
}

const NODE_TYPES = { vmNode: VmNodeView };

// ---------- main --------------------------------------------------------

export function ScenarioBuilder() {
  const [name, setName] = useState('My new scenario');
  const [slug, setSlug] = useState('my-scenario');
  const [version, setVersion] = useState('1.0.0');
  const [description, setDescription] = useState('Authored in the Forge visual builder.');

  // Segments are static for MVP - editing the segment list comes in v2.1
  // builder. _setSegments is reserved for then; underscored to silence
  // the unused-var lint until we wire the segment editor in.
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [segments, _setSegments] = useState<ReadonlyArray<SegmentDraft>>(DEFAULT_SEGMENTS);
  const [vms, setVms] = useState<ReadonlyArray<VmDraft>>([]);
  const [interSegRules, setInterSegRules] = useState<ReadonlyArray<InterSegRule>>([
    { from: 'external', to: 'dmz', action: 'allow', protocol: 'tcp', ports: [80, 443] },
    { from: 'dmz', to: 'internal', action: 'deny', protocol: 'any' },
  ]);
  const [selectedVmId, setSelectedVmId] = useState<string | null>(null);

  const cost = useMemo(
    () => vms.reduce((acc, v) => acc + (INSTANCE_COST_HR[v.instance_type] ?? 0.05) * (v.count ?? 1), 0),
    [vms],
  );

  const selectedVm = vms.find((v) => v.id === selectedVmId) ?? null;

  // ---- adding / removing VMs ----
  const addVm = useCallback((entry: PaletteEntry, overrideSegment?: string) => {
    setVms((prev) => {
      const i = prev.filter((v) => v.role === entry.role).length + 1;
      const hostname = entry.role.replace(/-[a-z]+$/, '').slice(0, 8) + `0${i}`;
      const id = `${entry.role}-${Date.now().toString(36)}-${i}`;
      const ami_ref = `ssm:/forge/dev/amis/${entry.role}/latest`;
      const targetSegment = overrideSegment ?? entry.defaultSegment;
      // Snap to next free y-slot in this segment so we never overlap with
      // an existing VM. Counts VMs already in the segment to compute idx.
      const existingInSeg = prev.filter((v) => v.network_segment === targetSegment);
      // Compute x from segment index (segments array order). If the segment
      // isn't in the static segments list, fall back to 0.
      const segIdx = DEFAULT_SEGMENTS.findIndex((s) => s.name === targetSegment);
      const colWidth = NODE_WIDTH + 80;
      const defaultX = (segIdx >= 0 ? segIdx : 0) * colWidth + 24 + 20;
      const defaultY = SEGMENT_HEADER + existingInSeg.length * (NODE_HEIGHT + 16);
      const newVm: VmDraft = {
        id,
        hostname,
        role: entry.role,
        ami_ref,
        instance_type: entry.defaultInstance,
        network_segment: targetSegment,
        depends_on: [],
        position: { x: defaultX, y: defaultY },
      };
      return [...prev, newVm];
    });
  }, []);

  // ---- drag-drop palette -> canvas ----
  const [dragHoverSegment, setDragHoverSegment] = useState<string | null>(null);

  const handlePaletteDragStart = useCallback(
    (e: React.DragEvent<HTMLDivElement>, entry: PaletteEntry) => {
      e.dataTransfer.setData('application/forge-vm-role', entry.role);
      e.dataTransfer.effectAllowed = 'copy';
    },
    [],
  );

  const handleCanvasDragOver = useCallback(
    (e: React.DragEvent<HTMLDivElement>) => {
      if (!e.dataTransfer.types.includes('application/forge-vm-role')) return;
      e.preventDefault();
      e.dataTransfer.dropEffect = 'copy';

      // Hit-test against segment columns by x-coord.
      const target = e.currentTarget.getBoundingClientRect();
      const x = e.clientX - target.left;
      const colWidth = NODE_WIDTH + 80;
      const idx = Math.min(Math.max(0, Math.floor((x - 24) / colWidth)), segments.length - 1);
      const seg = segments[idx]?.name ?? null;
      if (seg !== dragHoverSegment) setDragHoverSegment(seg);
    },
    [segments, dragHoverSegment],
  );

  const handleCanvasDrop = useCallback(
    (e: React.DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      const role = e.dataTransfer.getData('application/forge-vm-role');
      if (!role) return;
      const entry = PALETTE.find((p) => p.role === role);
      if (!entry) return;

      const target = e.currentTarget.getBoundingClientRect();
      const x = e.clientX - target.left;
      const colWidth = NODE_WIDTH + 80;
      const idx = Math.min(Math.max(0, Math.floor((x - 24) / colWidth)), segments.length - 1);
      const segName = segments[idx]?.name;

      addVm(entry, segName);
      setDragHoverSegment(null);
    },
    [segments, addVm],
  );

  const handleCanvasDragLeave = useCallback(() => {
    setDragHoverSegment(null);
  }, []);

  const updateVm = useCallback((id: string, patch: Partial<VmDraft>) => {
    setVms((prev) => prev.map((v) => (v.id === id ? { ...v, ...patch } : v)));
  }, []);

  const removeVm = useCallback((id: string) => {
    setVms((prev) => prev.filter((v) => v.id !== id));
    setSelectedVmId((cur) => (cur === id ? null : cur));
  }, []);

  // ---- ReactFlow layout: one column per segment ----
  const { nodes, edges } = useMemo(() => {
    const segmentX: Record<string, number> = {};
    segments.forEach((s, idx) => {
      segmentX[s.name] = idx * (NODE_WIDTH + 80) + 24;
    });

    const segmentNodes: Node[] = segments.map((s) => ({
      id: `seg-${s.name}`,
      type: 'group',
      data: { label: s.name },
      position: { x: segmentX[s.name] ?? 0, y: 0 },
      style: {
        backgroundColor: SEGMENT_COLOUR[s.kind] ?? '#15151a',
        border: '1px solid #2a2a2d',
        borderRadius: 12,
        width: NODE_WIDTH + 40,
        height: Math.max(160, SEGMENT_HEADER + vms.filter((v) => v.network_segment === s.name).length * (NODE_HEIGHT + 16) + 24),
        padding: 12,
      },
      draggable: false,
      selectable: false,
    }));

    const vmNodes: Node[] = vms.map((vm) => {
      const inSegment = vms.filter((v) => v.network_segment === vm.network_segment);
      const idxInSeg = inSegment.findIndex((v) => v.id === vm.id);
      const Icon = PALETTE.find((p) => p.role === vm.role)?.icon ?? Server;
      // Free positioning: each VM stores its own (x, y). When unset (just
      // added), default to the segment column slot. When the operator drags
      // the node, onNodeDragStop persists the new position via updateVm.
      const defaultX = (segmentX[vm.network_segment] ?? 0) + 20;
      const defaultY = SEGMENT_HEADER + idxInSeg * (NODE_HEIGHT + 16);
      return {
        id: vm.id,
        type: 'vmNode',
        data: { vm, Icon },
        position: vm.position ?? { x: defaultX, y: defaultY },
        draggable: true,
      };
    });

    // Segment-connecting edges: a "firewall" VM (linux-router / opnsense-fw)
    // that declares firewall_interfaces[] visibly connects the segments it
    // bridges. Edges from the firewall VM to one representative VM per other
    // attached segment - that's what the operator sees as "vlan A trusts vlan
    // B through this firewall".
    const segmentEdges: Edge[] = [];
    for (const vm of vms) {
      const fis = vm.firewall_interfaces;
      if (!fis || fis.length < 2) continue;
      // For each segment this firewall attaches to (other than the FW's own),
      // draw an edge from the firewall to a representative VM in that segment.
      const fwSegs = fis.map((fi) => fi?.segment).filter((s): s is string => Boolean(s));
      for (const segName of fwSegs) {
        if (!segName || segName === vm.network_segment) continue;
        const repTarget = vms.find(
          (v) => v.network_segment === segName && v.id !== vm.id,
        );
        if (!repTarget) continue;
        segmentEdges.push({
          id: `fw-${vm.id}-${segName}`,
          source: vm.id,
          target: repTarget.id,
          label: `${vm.network_segment} ⇄ ${segName}`,
          labelStyle: { fontFamily: "'JetBrains Mono', monospace", fontSize: 10, fill: colors.hudAmber },
          labelBgStyle: { fill: '#0a0a0c' },
          style: { stroke: colors.hudAmber, strokeWidth: 2, strokeDasharray: '4 2' },
          animated: true,
        });
      }
    }

    // Depends-on edges (existing behavior).
    const dependsOnEdges: Edge[] = vms.flatMap((vm) =>
      vm.depends_on.map((dep) => ({
        id: `dep-${dep}-${vm.id}`,
        source: dep,
        target: vm.id,
        style: { stroke: colors.hudCyan, strokeDasharray: '4 2', opacity: 0.5 },
      })),
    );

    // Domain-join edges (workstation -> DC). Crosses VLANs explicitly to
    // show the trust path. Operator clicks these to recall "the trainee box
    // in trainee-vlan trusts dc01 in mgmt-vlan".
    const domainJoinEdges: Edge[] = [];
    for (const vm of vms) {
      if (!vm.domain_join?.dc_vm_hostname) continue;
      const dcVm = vms.find((v) => v.hostname === vm.domain_join!.dc_vm_hostname);
      if (!dcVm) continue;
      domainJoinEdges.push({
        id: `domain-${vm.id}-${dcVm.id}`,
        source: vm.id,
        target: dcVm.id,
        label: `joins ${dcVm.dc_config?.domain_fqdn ?? dcVm.hostname}`,
        labelStyle: { fontFamily: "'JetBrains Mono', monospace", fontSize: 10, fill: colors.neon },
        labelBgStyle: { fill: '#0a0a0c' },
        style: { stroke: colors.neon, strokeWidth: 1.5 },
      });
    }

    return {
      nodes: [...segmentNodes, ...vmNodes],
      edges: [...segmentEdges, ...dependsOnEdges, ...domainJoinEdges],
    };
  }, [segments, vms]);

  // ---- drag persistence ----
  const handleNodeDragStop = useCallback(
    (_e: unknown, node: Node) => {
      if (node.type !== 'vmNode') return;
      // Pick the segment whose x-range contains the node's new position.
      const colWidth = NODE_WIDTH + 80;
      const newIdx = Math.min(
        Math.max(0, Math.floor((node.position.x - 24) / colWidth)),
        segments.length - 1,
      );
      const newSegment = segments[newIdx]?.name;
      updateVm(node.id, {
        position: { x: node.position.x, y: node.position.y },
        ...(newSegment ? { network_segment: newSegment } : {}),
      });
    },
    [segments, updateVm],
  );

  // ---- YAML emission ----
  const yaml = useMemo(
    () => emitYaml({ name, slug, version, description, segments, vms, interSegRules }),
    [name, slug, version, description, segments, vms, interSegRules],
  );

  // ---- live validate + cost-preview (F10) ----
  // Parse the emitted YAML into a plain document and feed it to the existing
  // /builder validate + cost-preview backends. Both queries re-fire on every
  // canvas/inspector change (yaml is recomputed) so validation + cost update
  // live. We gate on a parseable document so we never POST a half-edited doc.
  const scenarioDoc = useMemo(() => {
    try {
      return (parseYaml(yaml) as Record<string, unknown>) ?? null;
    } catch {
      return null;
    }
  }, [yaml]);

  const validateQ = useBuilderValidate({
    document: scenarioDoc ?? {},
    enabled: Boolean(scenarioDoc),
  });
  const costQ = useBuilderCostPreview({
    document: scenarioDoc ?? {},
    enabled: Boolean(scenarioDoc),
  });

  // ---- download ----
  const handleDownload = useCallback(() => {
    const blob = new Blob([yaml], { type: 'text/yaml' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${slug}.scenario.yaml`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }, [yaml, slug]);

  // ---- inter-segment rule editor ----
  const addRule = useCallback(() => {
    setInterSegRules((prev) => [
      ...prev,
      { from: segments[0]?.name ?? 'external', to: segments[1]?.name ?? 'dmz', action: 'allow', protocol: 'tcp', ports: [80] },
    ]);
  }, [segments]);

  const updateRule = useCallback((i: number, patch: Partial<InterSegRule>) => {
    setInterSegRules((prev) => prev.map((r, j) => (j === i ? { ...r, ...patch } : r)));
  }, []);

  const removeRule = useCallback((i: number) => {
    setInterSegRules((prev) => prev.filter((_, j) => j !== i));
  }, []);

  return (
    <PageContainer size="wide">
      <PageHeader
        icon={Blocks}
        title="Scenario builder"
        description="Drag VM roles onto the canvas, wire inter-segment rules, and export a v2-schema scenario.yaml. The locked schema validates topology, firewall, and MITRE coverage on import."
        breadcrumbs={[{ label: 'Forge' }, { label: 'Scenario builder' }]}
        actions={
          <div className="flex items-center gap-3">
            <div className="hidden flex-col items-end sm:flex">
              <span className="text-[11px] font-medium text-txt-muted">Live cost</span>
              <span className="font-mono text-[16px] font-bold text-neon">
                ${(costQ.data?.hourly_usd ?? cost).toFixed(2)}/hr
              </span>
            </div>
            {/* F8: the header "Validate" button was a no-op — validation runs
                live in the ValidatePane side panel, so this was a confusing
                second Validate affordance. Removed. */}
            <Button variant="primary" leftIcon={<Download className="size-4" />} onClick={handleDownload}>
              Download YAML
            </Button>
          </div>
        }
      />

      <Section
        title="Topology"
        description="Compose the range: a VM palette, the network canvas, and a per-VM inspector."
      >
      <Grid gutter="md">
        {/* ---------- palette ---------- */}
        <Grid.Col span={{ base: 12, md: 2 }}>
          <Card>
            <Stack gap="sm">
              <Group gap="xs">
                <Server size={15} color={colors.hudCyan} />
                <Text fw={600} size="sm">
                  VM roles
                </Text>
              </Group>
              <Stack gap={4}>
                {PALETTE.map((entry) => {
                  const Icon = entry.icon;
                  return (
                    <div
                      key={entry.role}
                      draggable
                      onDragStart={(e) => handlePaletteDragStart(e, entry)}
                      title={`Drag onto canvas, or click to add to ${entry.defaultSegment}`}
                      style={{ cursor: 'grab' }}
                    >
                      <Button
                        variant="secondary"
                        size="xs"
                        leftIcon={<Icon size={12} />}
                        onClick={() => addVm(entry)}
                      >
                        {entry.label}
                      </Button>
                    </div>
                  );
                })}
              </Stack>
              <Text size="xs" c="dimmed" mt={4}>
                Drag onto a segment column on the canvas, or click to drop in the role's default
                segment.
              </Text>
            </Stack>
          </Card>
        </Grid.Col>

        {/* ---------- canvas ---------- */}
        <Grid.Col span={{ base: 12, md: 7 }}>
          <Card>
            <Stack gap="sm">
              <Group justify="space-between" wrap="wrap" gap="sm">
                <Group gap="sm" wrap="wrap">
                  <TextInput
                    value={name}
                    onChange={(e) => setName(e.currentTarget.value)}
                    label="Name"
                    size="xs"
                  />
                  <TextInput
                    value={slug}
                    onChange={(e) => setSlug(e.currentTarget.value)}
                    label="Slug"
                    size="xs"
                  />
                  <TextInput
                    value={version}
                    onChange={(e) => setVersion(e.currentTarget.value)}
                    label="Version"
                    size="xs"
                  />
                </Group>
                <Group gap="xs">
                  <Badge variant="info" size="xs">
                    {vms.length} VM{vms.length === 1 ? '' : 's'}
                  </Badge>
                  <Badge variant="neutral" size="xs">
                    {segments.length} segments
                  </Badge>
                  <Badge variant="warn" size="xs">
                    {interSegRules.length} rules
                  </Badge>
                </Group>
              </Group>
              <Box
                style={{
                  height: 460,
                  border: `1px solid ${dragHoverSegment ? colors.hudCyan : '#1f1f23'}`,
                  borderRadius: 8,
                  background: '#0a0a0c',
                  position: 'relative',
                  transition: 'border-color 120ms ease',
                }}
                onDragOver={handleCanvasDragOver}
                onDragLeave={handleCanvasDragLeave}
                onDrop={handleCanvasDrop}
              >
                <ReactFlow
                  nodes={nodes}
                  edges={edges}
                  nodeTypes={NODE_TYPES}
                  onNodeClick={(_e, n) => {
                    if (n.type === 'vmNode') setSelectedVmId(n.id);
                  }}
                  onNodeDragStop={handleNodeDragStop}
                  nodesDraggable
                  nodesConnectable={false}
                  fitView
                  proOptions={{ hideAttribution: true }}
                >
                  <Background variant={BackgroundVariant.Dots} gap={20} size={1} color="#1c1c20" />
                  <Controls showInteractive={false} />
                </ReactFlow>
                {dragHoverSegment && (
                  <Box
                    style={{
                      position: 'absolute',
                      top: 10,
                      left: '50%',
                      transform: 'translateX(-50%)',
                      pointerEvents: 'none',
                      background: alpha('hudCyan', 0.12),
                      border: `1px solid ${colors.hudCyan}`,
                      borderRadius: 8,
                      padding: '5px 12px',
                      fontFamily: "'Plus Jakarta Sans', ui-sans-serif, system-ui, sans-serif",
                      fontSize: 12,
                      fontWeight: 600,
                      color: colors.hudCyan,
                    }}
                  >
                    Drop in {dragHoverSegment}
                  </Box>
                )}
              </Box>
              {vms.length === 0 && (
                <Alert color="cyan" variant="light" icon={<AlertTriangle size={14} />}>
                  <b>Drag a VM role</b> from the palette onto the canvas — drop on a segment column
                  to place it there, or click a palette button to use the default segment.
                </Alert>
              )}
            </Stack>
          </Card>
        </Grid.Col>

        {/* ---------- inspector ---------- */}
        <Grid.Col span={{ base: 12, md: 3 }}>
          <Card>
            <Stack gap="sm">
              <Group gap="xs">
                <Workflow size={15} color={colors.hudAmber} />
                <Text fw={600} size="sm">
                  Inspector
                </Text>
              </Group>
              {!selectedVm ? (
                <Text size="xs" c="dimmed">
                  Click a VM on the canvas to edit its properties.
                </Text>
              ) : (
                <Stack gap="sm">
                  <TextInput
                    label="Hostname"
                    value={selectedVm.hostname}
                    onChange={(e) => updateVm(selectedVm.id, { hostname: e.currentTarget.value })}
                    size="xs"
                  />
                  <Select
                    label="Role"
                    value={selectedVm.role}
                    onChange={(v) => v && updateVm(selectedVm.id, { role: v, ami_ref: `ssm:/forge/dev/amis/${v}/latest` })}
                    data={PALETTE.map((p) => ({ value: p.role, label: p.role }))}
                    size="xs"
                  />
                  <Select
                    label="Instance type"
                    value={selectedVm.instance_type}
                    onChange={(v) => v && updateVm(selectedVm.id, { instance_type: v })}
                    data={INSTANCE_TYPES.map((it) => ({ value: it, label: it }))}
                    size="xs"
                  />
                  <Select
                    label="Network segment"
                    value={selectedVm.network_segment}
                    onChange={(v) => v && updateVm(selectedVm.id, { network_segment: v })}
                    data={segments.map((s) => ({ value: s.name, label: `${s.name} (${s.kind})` }))}
                    size="xs"
                  />
                  <NumberInput
                    label="Count (replicas)"
                    value={selectedVm.count ?? 1}
                    onChange={(v) => updateVm(selectedVm.id, { count: typeof v === 'number' ? v : 1 })}
                    min={1}
                    max={32}
                    size="xs"
                  />

                  {/* ------ DC-specific fields (role: windows-dc) ------ */}
                  {selectedVm.role === 'windows-dc' && (
                    <>
                      <Divider label="Domain Controller" labelPosition="left" />
                      <TextInput
                        label="Domain FQDN"
                        placeholder="forge.local"
                        value={selectedVm.dc_config?.domain_fqdn ?? ''}
                        onChange={(e) =>
                          updateVm(selectedVm.id, {
                            dc_config: {
                              ...(selectedVm.dc_config ?? { domain_fqdn: '' }),
                              domain_fqdn: e.currentTarget.value,
                            },
                          })
                        }
                        size="xs"
                      />
                      <TextInput
                        label="NetBIOS name"
                        placeholder="FORGE"
                        value={selectedVm.dc_config?.netbios ?? ''}
                        onChange={(e) =>
                          updateVm(selectedVm.id, {
                            dc_config: {
                              ...(selectedVm.dc_config ?? { domain_fqdn: '' }),
                              netbios: e.currentTarget.value,
                            },
                          })
                        }
                        size="xs"
                      />
                      <TextInput
                        label="OU tree (one OU per line)"
                        placeholder="HQ,IT&#10;HQ,Finance&#10;HQ,Workstations"
                        value={selectedVm.dc_config?.ou_tree ?? ''}
                        onChange={(e) =>
                          updateVm(selectedVm.id, {
                            dc_config: {
                              ...(selectedVm.dc_config ?? { domain_fqdn: '' }),
                              ou_tree: e.currentTarget.value,
                            },
                          })
                        }
                        size="xs"
                      />
                    </>
                  )}

                  {/* ------ Workstation domain-join (cross-VLAN allowed) ------ */}
                  {(selectedVm.role === 'windows-workstation' ||
                    selectedVm.role === 'base-windows-10') &&
                    vms.some((v) => v.role === 'windows-dc') && (
                      <>
                        <Divider label="Domain join" labelPosition="left" />
                        <Select
                          label="Join domain controller"
                          placeholder="Workgroup (not domain-joined)"
                          value={selectedVm.domain_join?.dc_vm_hostname ?? null}
                          onChange={(v) => {
                            const patch: Partial<VmDraft> = {};
                            if (v) {
                              patch.domain_join = {
                                dc_vm_hostname: v,
                                ou: selectedVm.domain_join?.ou ?? '',
                              };
                            } else {
                              const next = { ...selectedVm };
                              delete next.domain_join;
                              setVms((prev) =>
                                prev.map((p) => (p.id === selectedVm.id ? next : p)),
                              );
                              return;
                            }
                            updateVm(selectedVm.id, patch);
                          }}
                          data={vms
                            .filter((v) => v.role === 'windows-dc')
                            .map((v) => ({
                              value: v.hostname,
                              label: `${v.hostname}${v.dc_config?.domain_fqdn ? ` (${v.dc_config.domain_fqdn})` : ''}`,
                            }))}
                          clearable
                          size="xs"
                        />
                        {selectedVm.domain_join && (
                          <TextInput
                            label="Target OU (optional)"
                            placeholder="HQ/Workstations"
                            value={selectedVm.domain_join.ou ?? ''}
                            onChange={(e) =>
                              updateVm(selectedVm.id, {
                                domain_join: {
                                  ...selectedVm.domain_join!,
                                  ou: e.currentTarget.value,
                                },
                              })
                            }
                            size="xs"
                          />
                        )}
                      </>
                    )}

                  {/* ------ Firewall-specific interface bindings ------ */}
                  {(selectedVm.role === 'linux-router' ||
                    selectedVm.role === 'opnsense-fw') && (
                    <>
                      <Divider label="Firewall interfaces" labelPosition="left" />
                      <Text size="xs" c="dimmed">
                        Each interface attaches the firewall to one segment.
                        Pairs of segments become visible edges on the canvas.
                      </Text>
                      <Stack gap={4}>
                        {(selectedVm.firewall_interfaces ?? []).map((fi, i) => (
                          <Group key={i} gap={4} wrap="nowrap">
                            <Select
                              value={fi.segment}
                              onChange={(v) => {
                                if (!v) return;
                                const next = [...(selectedVm.firewall_interfaces ?? [])];
                                next[i] = { ...next[i]!, segment: v };
                                updateVm(selectedVm.id, { firewall_interfaces: next });
                              }}
                              data={segments.map((s) => s.name)}
                              size="xs"
                              w={110}
                            />
                            <Select
                              value={fi.role}
                              onChange={(v) => {
                                if (!v) return;
                                const next = [...(selectedVm.firewall_interfaces ?? [])];
                                next[i] = { ...next[i]!, role: v as FirewallInterfaceBinding['role'] };
                                updateVm(selectedVm.id, { firewall_interfaces: next });
                              }}
                              data={['wan', 'lan', 'dmz', 'mgmt', 'trunk']}
                              size="xs"
                              w={70}
                            />
                            <Button
                              variant="ghost"
                              size="xs"
                              leftIcon={<Trash2 size={10} />}
                              onClick={() => {
                                const next = (selectedVm.firewall_interfaces ?? []).filter(
                                  (_, j) => j !== i,
                                );
                                updateVm(selectedVm.id, { firewall_interfaces: next });
                              }}
                            >
                              x
                            </Button>
                          </Group>
                        ))}
                        <Button
                          variant="secondary"
                          size="xs"
                          leftIcon={<Plus size={10} />}
                          onClick={() => {
                            const next = [
                              ...(selectedVm.firewall_interfaces ?? []),
                              { segment: segments[0]?.name ?? 'external', role: 'lan' as const },
                            ];
                            updateVm(selectedVm.id, { firewall_interfaces: next });
                          }}
                        >
                          Add interface
                        </Button>
                      </Stack>
                    </>
                  )}

                  {/* ------ Per-VM firewall rules (host-level) ------ */}
                  <Divider label="Per-VM firewall rules" labelPosition="left" />
                  <Text size="xs" c="dimmed">
                    Host-level rules applied to this VM's iptables/Windows
                    Firewall at deploy. Stacks on top of segment-level rules.
                  </Text>
                  <Stack gap={4}>
                    {(selectedVm.firewall_rules ?? []).map((r, i) => (
                      <Group key={i} gap={4} wrap="nowrap">
                        <Select
                          value={r.direction}
                          onChange={(v) => {
                            if (!v) return;
                            const next = [...(selectedVm.firewall_rules ?? [])];
                            next[i] = { ...next[i]!, direction: v as 'inbound' | 'outbound' };
                            updateVm(selectedVm.id, { firewall_rules: next });
                          }}
                          data={['inbound', 'outbound']}
                          size="xs"
                          w={85}
                        />
                        <Select
                          value={r.action}
                          onChange={(v) => {
                            if (!v) return;
                            const next = [...(selectedVm.firewall_rules ?? [])];
                            next[i] = { ...next[i]!, action: v as 'allow' | 'deny' };
                            updateVm(selectedVm.id, { firewall_rules: next });
                          }}
                          data={['allow', 'deny']}
                          size="xs"
                          w={75}
                        />
                        <Select
                          value={r.protocol}
                          onChange={(v) => {
                            if (!v) return;
                            const next = [...(selectedVm.firewall_rules ?? [])];
                            next[i] = { ...next[i]!, protocol: v as PerVmFirewallRule['protocol'] };
                            updateVm(selectedVm.id, { firewall_rules: next });
                          }}
                          data={['tcp', 'udp', 'icmp', 'any']}
                          size="xs"
                          w={70}
                        />
                        <TextInput
                          placeholder="target"
                          value={r.target}
                          onChange={(e) => {
                            const next = [...(selectedVm.firewall_rules ?? [])];
                            next[i] = { ...next[i]!, target: e.currentTarget.value };
                            updateVm(selectedVm.id, { firewall_rules: next });
                          }}
                          size="xs"
                          w={90}
                        />
                        <TextInput
                          placeholder="ports"
                          value={(r.ports ?? []).join(',')}
                          onChange={(e) => {
                            const txt = e.currentTarget.value.trim();
                            const next = [...(selectedVm.firewall_rules ?? [])];
                            const ports = txt
                              ? txt.split(',').map((s) => parseInt(s.trim(), 10)).filter((n) => Number.isFinite(n))
                              : undefined;
                            next[i] = ports !== undefined ? { ...next[i]!, ports } : { ...next[i]! };
                            if (ports === undefined) delete (next[i] as Partial<PerVmFirewallRule>).ports;
                            updateVm(selectedVm.id, { firewall_rules: next });
                          }}
                          size="xs"
                          w={70}
                        />
                        <Button
                          variant="ghost"
                          size="xs"
                          onClick={() => {
                            const next = (selectedVm.firewall_rules ?? []).filter(
                              (_, j) => j !== i,
                            );
                            updateVm(selectedVm.id, { firewall_rules: next });
                          }}
                        >
                          x
                        </Button>
                      </Group>
                    ))}
                    <Button
                      variant="secondary"
                      size="xs"
                      leftIcon={<Plus size={10} />}
                      onClick={() => {
                        const next = [
                          ...(selectedVm.firewall_rules ?? []),
                          { direction: 'inbound' as const, action: 'allow' as const, protocol: 'tcp' as const, target: 'any' },
                        ];
                        updateVm(selectedVm.id, { firewall_rules: next });
                      }}
                    >
                      Add rule
                    </Button>
                  </Stack>

                  {/* ------ Agents installed on this VM ------ */}
                  <Divider label="Agents" labelPosition="left" />
                  <Text size="xs" c="dimmed">
                    Agents installed at deploy. Each points at a server VM
                    in the scenario; SSM resolves the private IP at runtime.
                  </Text>
                  <Select
                    label="Wazuh agent → manager"
                    placeholder="No Wazuh agent"
                    value={selectedVm.agents?.wazuh?.manager_vm ?? null}
                    onChange={(v) => {
                      const agents = { ...(selectedVm.agents ?? {}) };
                      if (v) {
                        agents.wazuh = { manager_vm: v };
                      } else {
                        delete agents.wazuh;
                      }
                      updateVm(selectedVm.id, { agents });
                    }}
                    data={vms
                      .filter((v) => v.role === 'wazuh-manager')
                      .map((v) => ({ value: v.hostname, label: v.hostname }))}
                    clearable
                    size="xs"
                  />
                  <Select
                    label="Elastic agent → SIEM"
                    placeholder="No Elastic agent"
                    value={selectedVm.agents?.elastic?.manager_vm ?? null}
                    onChange={(v) => {
                      const agents = { ...(selectedVm.agents ?? {}) };
                      if (v) {
                        agents.elastic = { manager_vm: v };
                      } else {
                        delete agents.elastic;
                      }
                      updateVm(selectedVm.id, { agents });
                    }}
                    data={vms
                      .filter((v) => v.role === 'elastic-stack')
                      .map((v) => ({ value: v.hostname, label: v.hostname }))}
                    clearable
                    size="xs"
                  />
                  <Select
                    label="Caldera agent (Sandcat) → server"
                    placeholder="No Caldera agent"
                    value={selectedVm.agents?.caldera?.server_vm ?? null}
                    onChange={(v) => {
                      const agents = { ...(selectedVm.agents ?? {}) };
                      if (v) {
                        agents.caldera = { server_vm: v };
                      } else {
                        delete agents.caldera;
                      }
                      updateVm(selectedVm.id, { agents });
                    }}
                    data={vms
                      .filter((v) => v.role === 'caldera-attacker')
                      .map((v) => ({ value: v.hostname, label: v.hostname }))}
                    clearable
                    size="xs"
                  />
                  <Checkbox
                    label="Install Sysmon (Windows only)"
                    checked={Boolean(selectedVm.agents?.sysmon)}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                      const agents = { ...(selectedVm.agents ?? {}) };
                      if (e.currentTarget.checked) {
                        agents.sysmon = true;
                      } else {
                        delete agents.sysmon;
                      }
                      updateVm(selectedVm.id, { agents });
                    }}
                    size="xs"
                    disabled={
                      selectedVm.role !== 'windows-workstation' &&
                      selectedVm.role !== 'windows-dc' &&
                      selectedVm.role !== 'base-windows-10'
                    }
                  />

                  <Divider />
                  <Button
                    variant="ghost"
                    size="xs"
                    leftIcon={<Trash2 size={12} />}
                    onClick={() => removeVm(selectedVm.id)}
                  >
                    Remove VM
                  </Button>
                </Stack>
              )}
              <Divider />
              <Stack gap={4}>
                <Text fw={600} size="xs" c="dimmed">
                  Description
                </Text>
                <TextInput
                  value={description}
                  onChange={(e) => setDescription(e.currentTarget.value)}
                  size="xs"
                />
              </Stack>
            </Stack>
          </Card>
        </Grid.Col>
      </Grid>
      </Section>

      {/* ---------- inter-segment rules ---------- */}
      <Section
        title="Inter-segment rules"
        description="How traffic flows between network segments — evaluated top to bottom."
        action={
          <Button variant="secondary" size="sm" leftIcon={<Plus className="size-4" />} onClick={addRule}>
            Add rule
          </Button>
        }
      >
        <Card>
          <Stack gap={4}>
            {interSegRules.length === 0 && (
              <Text size="sm" c="dimmed">
                No inter-segment rules yet. Add a rule to define how segments talk to each other.
              </Text>
            )}
            {interSegRules.map((rule, i) => (
              <Group key={i} gap="xs" wrap="nowrap">
                <Select
                  value={rule.from}
                  onChange={(v) => v && updateRule(i, { from: v })}
                  data={segments.map((s) => s.name)}
                  size="xs"
                  w={120}
                />
                <Text size="xs" c="dimmed">
                  →
                </Text>
                <Select
                  value={rule.to}
                  onChange={(v) => v && updateRule(i, { to: v })}
                  data={segments.map((s) => s.name)}
                  size="xs"
                  w={120}
                />
                <Select
                  value={rule.action}
                  onChange={(v) => v && updateRule(i, { action: v as 'allow' | 'deny' })}
                  data={['allow', 'deny']}
                  size="xs"
                  w={90}
                />
                <Select
                  value={rule.protocol}
                  onChange={(v) => v && updateRule(i, { protocol: v as InterSegRule['protocol'] })}
                  data={['tcp', 'udp', 'icmp', 'any']}
                  size="xs"
                  w={90}
                />
                <TextInput
                  placeholder="80,443"
                  value={(rule.ports ?? []).join(',')}
                  onChange={(e) => {
                    const txt = e.currentTarget.value.trim();
                    if (txt) {
                      const ports = txt
                        .split(',')
                        .map((s) => parseInt(s.trim(), 10))
                        .filter((n) => Number.isFinite(n) && n > 0 && n <= 65535);
                      updateRule(i, { ports });
                    } else {
                      updateRule(i, {});
                    }
                  }}
                  size="xs"
                  w={120}
                />
                <Button variant="ghost" size="xs" leftIcon={<Trash2 size={12} />} onClick={() => removeRule(i)}>
                  Remove
                </Button>
              </Group>
            ))}
          </Stack>
        </Card>
      </Section>

      <motion.div
        variants={staggerContainer}
        initial="hidden"
        animate="show"
        className="grid grid-cols-1 gap-6 xl:grid-cols-2"
      >
        {/* ---------- YAML preview ---------- */}
        <motion.div variants={cardItem}>
          <Section
            title="scenario.yaml"
            description="Live preview of the exported scenario document."
          >
            <Card>
              <Box
                component="pre"
                style={{
                  background: 'rgb(var(--c-deep))',
                  border: '1px solid var(--c-bd)',
                  borderRadius: 10,
                  fontFamily: "'JetBrains Mono', ui-monospace, monospace",
                  fontSize: 11.5,
                  lineHeight: 1.6,
                  color: 'rgb(var(--c-txt-2))',
                  maxHeight: 460,
                  overflow: 'auto',
                  whiteSpace: 'pre',
                  margin: 0,
                  padding: 16,
                }}
              >
                {yaml}
              </Box>
            </Card>
          </Section>
        </motion.div>

        {/* ---------- live validation + cost (F10) ---------- */}
        <motion.div variants={cardItem} className="space-y-6">
          <Section
            title="Validation"
            description="The locked schema checks topology, firewall, and MITRE coverage."
          >
            <Card>
              <ValidatePane
                isLoading={validateQ.isPending}
                valid={validateQ.data?.valid}
                errors={validateQ.data?.errors}
                warnings={validateQ.data?.warnings}
              />
            </Card>
          </Section>

          <Section
            title="Cost breakdown"
            description="Estimated hourly spend across the range's instances."
          >
            <Card>
              <CostPane isLoading={costQ.isPending} cost={costQ.data} />
            </Card>
          </Section>
        </motion.div>
      </motion.div>
    </PageContainer>
  );
}

// ---------- YAML emitter ------------------------------------------------

function indent(level: number): string {
  return '  '.repeat(level);
}

function yamlString(s: string): string {
  // Defensive: quote if contains anything that needs quoting in YAML 1.2.
  if (s === '' || /[:#&*!,?\-\[\]{}|>"'`%@\n]/.test(s)) {
    return JSON.stringify(s);
  }
  return s;
}

function emitYaml(args: {
  name: string;
  slug: string;
  version: string;
  description: string;
  segments: ReadonlyArray<SegmentDraft>;
  vms: ReadonlyArray<VmDraft>;
  interSegRules: ReadonlyArray<InterSegRule>;
}): string {
  const { name, slug, version, description, segments, vms, interSegRules } = args;
  const lines: string[] = [];

  lines.push(`apiVersion: forge.cyberelites.ae/v1`);
  lines.push(`kind: Scenario`);
  lines.push(`metadata:`);
  lines.push(`${indent(1)}name: ${yamlString(name)}`);
  lines.push(`${indent(1)}slug: ${yamlString(slug)}`);
  lines.push(`${indent(1)}version: ${yamlString(version)}`);
  lines.push(`${indent(1)}description: |`);
  for (const line of description.split('\n')) {
    lines.push(`${indent(2)}${line}`);
  }
  lines.push(`${indent(1)}difficulty: intermediate`);

  // network
  lines.push(`network:`);
  lines.push(`${indent(1)}segments:`);
  for (const seg of segments) {
    lines.push(`${indent(2)}- name: ${yamlString(seg.name)}`);
    lines.push(`${indent(3)}kind: ${yamlString(seg.kind)}`);
    if (seg.cidr_override) lines.push(`${indent(3)}cidr_override: ${yamlString(seg.cidr_override)}`);
    if (typeof seg.vlan_id === 'number') lines.push(`${indent(3)}vlan_id: ${seg.vlan_id}`);
    if (seg.public_facing) lines.push(`${indent(3)}public_facing: true`);
  }
  lines.push(`${indent(1)}inter_segment_rules:`);
  for (const rule of interSegRules) {
    lines.push(`${indent(2)}- from: ${yamlString(rule.from)}`);
    lines.push(`${indent(3)}to: ${yamlString(rule.to)}`);
    lines.push(`${indent(3)}action: ${rule.action}`);
    lines.push(`${indent(3)}protocol: ${rule.protocol}`);
    if (rule.ports && rule.ports.length > 0) {
      lines.push(`${indent(3)}ports: [${rule.ports.join(', ')}]`);
    }
  }

  // topology.vms
  lines.push(`topology:`);
  lines.push(`${indent(1)}vms:`);
  for (const vm of vms) {
    lines.push(`${indent(2)}- name: ${yamlString(vm.hostname)}`);
    lines.push(`${indent(3)}role: ${yamlString(vm.role)}`);
    lines.push(`${indent(3)}ami_ref: ${yamlString(vm.ami_ref)}`);
    lines.push(`${indent(3)}instance_type: ${yamlString(vm.instance_type)}`);
    lines.push(`${indent(3)}network_segment: ${yamlString(vm.network_segment)}`);
    if (vm.depends_on.length > 0) {
      lines.push(`${indent(3)}depends_on: [${vm.depends_on.map(yamlString).join(', ')}]`);
    }
    if (vm.count && vm.count > 1) {
      lines.push(`${indent(3)}count: ${vm.count}`);
    }
    // DC config
    if (vm.dc_config && vm.dc_config.domain_fqdn) {
      lines.push(`${indent(3)}dc_config:`);
      lines.push(`${indent(4)}domain_fqdn: ${yamlString(vm.dc_config.domain_fqdn)}`);
      if (vm.dc_config.netbios) {
        lines.push(`${indent(4)}netbios: ${yamlString(vm.dc_config.netbios)}`);
      }
      if (vm.dc_config.ou_tree) {
        lines.push(`${indent(4)}ou_tree: |`);
        for (const line of vm.dc_config.ou_tree.split('\n')) {
          lines.push(`${indent(5)}${line}`);
        }
      }
    }
    // Domain join (workstations) - tracks ANY vm hostname including ones
    // in different VLANs so the cross-VLAN trust path is explicit.
    if (vm.domain_join?.dc_vm_hostname) {
      lines.push(`${indent(3)}domain_join:`);
      lines.push(`${indent(4)}dc_vm: ${yamlString(vm.domain_join.dc_vm_hostname)}`);
      if (vm.domain_join.ou) {
        lines.push(`${indent(4)}ou: ${yamlString(vm.domain_join.ou)}`);
      }
    }
    // Firewall interface bindings (linux-router / opnsense-fw)
    if (vm.firewall_interfaces && vm.firewall_interfaces.length > 0) {
      lines.push(`${indent(3)}firewall:`);
      lines.push(`${indent(4)}interfaces:`);
      for (const fi of vm.firewall_interfaces) {
        lines.push(`${indent(5)}- { segment: ${yamlString(fi.segment)}, role: ${fi.role} }`);
      }
    }
    // Per-VM firewall rules
    if (vm.firewall_rules && vm.firewall_rules.length > 0) {
      lines.push(`${indent(3)}firewall_rules:`);
      for (const r of vm.firewall_rules) {
        const ports = r.ports && r.ports.length > 0 ? `, ports: [${r.ports.join(', ')}]` : '';
        lines.push(
          `${indent(4)}- { direction: ${r.direction}, action: ${r.action}, protocol: ${r.protocol}, target: ${yamlString(r.target)}${ports} }`,
        );
      }
    }
    // Agent installs (Wazuh / Elastic / Caldera / Sysmon)
    if (vm.agents && Object.keys(vm.agents).length > 0) {
      lines.push(`${indent(3)}agents:`);
      if (vm.agents.wazuh?.manager_vm) {
        lines.push(`${indent(4)}wazuh:`);
        lines.push(`${indent(5)}manager_vm: ${yamlString(vm.agents.wazuh.manager_vm)}`);
      }
      if (vm.agents.elastic?.manager_vm) {
        lines.push(`${indent(4)}elastic:`);
        lines.push(`${indent(5)}manager_vm: ${yamlString(vm.agents.elastic.manager_vm)}`);
        if (vm.agents.elastic.policy_id) {
          lines.push(`${indent(5)}policy_id: ${yamlString(vm.agents.elastic.policy_id)}`);
        }
      }
      if (vm.agents.caldera?.server_vm) {
        lines.push(`${indent(4)}caldera:`);
        lines.push(`${indent(5)}server_vm: ${yamlString(vm.agents.caldera.server_vm)}`);
      }
      if (vm.agents.sysmon) {
        lines.push(`${indent(4)}sysmon: true`);
      }
    }
  }

  // placeholders for phases/objectives/detections (operator adds these)
  lines.push(`phases: []`);
  lines.push(`objectives: []`);
  lines.push(`detections: []`);

  return lines.join('\n') + '\n';
}
